<script setup>
import {ref, reactive, computed, watch, onMounted, toRaw, onBeforeUnmount, provide} from 'vue'
import {useRoute, useRouter} from "vue-router";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
import Attachments from "@/views/orders/Details/Attachments.vue";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {Icon} from "@iconify/vue";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import Tooltip from "@/components/Tooltip/index.vue";
import {showMessage} from "@/utils/showMessage.js";
import Loading from "@/components/Icon/Loading.vue";
import showAlert from "@/utils/showAlert.js";
import DatePicker from "primevue/datepicker";
import Customers from "@/components/Customers.vue";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {useUserStore} from "@/stores/user.js";
import CustomerProfile from "@/components/CustomerProfile/index.vue";
import {useCustomerStore} from "@/stores/customer.js";
import FloatLabel from "primevue/floatlabel";
import {InputGroup, InputGroupAddon, InputText, Textarea} from "primevue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {truncateTo2} from "@/utils/truncateTo2.js";

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)

/* attachments */
const attachments = ref(null)

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]

/* ---------- Stores ---------- */
const ordersStore = useMainOrdersStore()
const userStore = useUserStore();
const customerStore = useCustomerStore();
const configStore = useConfigStore()
const currencyPricesStore = useCurrencyPricesStore();
const route = useRoute()
const router = useRouter()

/* ---------- Loading status ---------- */
const {
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()
const loading = ref(false)
const saving = ref(false)
const isUploadingAttachment = ref(false)

/* ---------- Initial version of the order for comparison ---------- */
let initialOrder = {}   // شیء ساده (غیر‑ reactive)

/* ---------- Order object ---------- */
const order = reactive({
  state: 'New',
  title: '',
  customer: '',
  customerID: '',
  usd_tmn: '',
  price: '',
  cost: '',
  revenue: '',
  tomanPrice: '',
  tomanCost: '',
  tomanRevenue: '',
  reminderTime: '',
  notes: '',
})

const priceInputSource = ref(null)
const costInputSource = ref(null)

const rules = computed(() => {
  return {
    state: {required},
    title: {required},
    usd_tmn: {required, numeric},
    tomanPrice: {required, numeric},
    tomanCost: {required, numeric},
    customerID: {required},
  };
});
const $vulidate = useVuelidate(rules, order);

const selectedCustomer = ref({})
const isShowCustomerProfileModal = ref(false);
const canShowCustomerProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))
const currencyPrices = ref([])
const activeUsdRateName = ref('Toman Shop')
const nextUsdRateName = computed(() => activeUsdRateName.value === 'Toman Shop' ? 'Toman' : 'Toman Shop')
const canToggleUsdRate = computed(() => currencyPrices.value.some(currency => currency.name === nextUsdRateName.value))

/* ---------- Related to state ---------- */
const isStateOpen = ref(false)
const stateDropdownRef = ref(null)
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}
const toggleStateDropdown = () => isStateOpen.value = !isStateOpen.value

const selectState = (state) => {
  order.state = state
  isStateOpen.value = false
}

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

const handleClickOutside = (e) => {
  if (stateDropdownRef.value && !stateDropdownRef.value.contains(event.target)) isStateOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

/* ---------- Format / Parse ---------- */
const formatText = (text) => {
  return text?.replace(/\n/g, '<br>') || ''
}

/* ---------- Calculations ---------- */
function recalc() {
  const usd = parseFloat(order.usd_tmn)

  if (priceInputSource.value === 'price') {
    const price = parseFloat(order.price)
    order.tomanPrice = isFinite(price) && isFinite(usd)
        ? String(Math.ceil(price * usd / 1000))
        : ''
  } else {
    const tomanPrice = parseFloat(order.tomanPrice)
    order.price = isFinite(tomanPrice) && isFinite(usd)
        ? String(truncateTo2(1000 * tomanPrice / usd))
        : ''
  }

  if (costInputSource.value === 'cost') {
    const cost = parseFloat(order.cost)
    order.tomanCost = isFinite(cost) && isFinite(usd)
        ? String(Math.ceil(cost * usd / 1000))
        : ''
  } else {
    const tomanCost = parseFloat(order.tomanCost)
    order.cost = isFinite(tomanCost) && isFinite(usd)
        ? String(truncateTo2(1000 * tomanCost / usd))
        : ''
  }

  const tp = parseFloat(order.tomanPrice)
  const tc = parseFloat(order.tomanCost)

  if (isFinite(tp) && isFinite(tc)) {
    order.tomanRevenue = String(Math.floor(tp - tc))
  } else {
    order.tomanRevenue = ''
  }

  const trCalc = parseFloat(order.tomanRevenue)
  if (isFinite(trCalc) && isFinite(usd)) {
    order.revenue = String(truncateTo2(1000 * trCalc / usd))
  } else {
    order.revenue = ''
  }
}

function handlePriceInput(value) {
  if (value === '') {
    priceInputSource.value = null
    order.tomanPrice = ''
  } else {
    priceInputSource.value = 'price'
  }
  recalc()
}

function handleTomanPriceInput(value) {
  if (value === '') {
    priceInputSource.value = null
    order.price = ''
  } else {
    priceInputSource.value = 'tomanPrice'
  }
  recalc()
}

function handleCostInput(value) {
  if (value === '') {
    costInputSource.value = null
    order.tomanCost = ''
  } else {
    costInputSource.value = 'cost'
  }
  recalc()
}

function handleTomanCostInput(value) {
  if (value === '') {
    costInputSource.value = null
    order.cost = ''
  } else {
    costInputSource.value = 'tomanCost'
  }
  recalc()
}

/* Object filter: Keep only keys that have the value truthy */
function buildPayload() {
  const payload = {}
  Object.entries(order).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })
  if (attachments.value.length) {
    payload.attachments = {paymentReceipts: attachments.value}
  }
  return payload
}

async function onSave() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  // 3) User verification
  //const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  //if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.addOrder('Shop', payload)

    if (res === true) {
      showMessage('New Order added!', 'success')
      backToOrders();
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

function backToOrders() {
  router.push({name: 'shop-orders'})
}

/* ---------- Related to reminderTime ---------- */
const reminderTimeModel = computed({
  get() {
    if (order.reminderTime && !isNaN(order.reminderTime)) {
      return new Date(Number(order.reminderTime));
    }
    return null;
  },
  set(value) {
    if (value instanceof Date) {
      order.reminderTime = value.getTime();
    } else {
      order.reminderTime = '';
    }
  }
})

function selectNow() {
  const now = new Date();
  order.reminderTime = now.getTime();
}

function clearReminderTime() {
  order.reminderTime = 0;
}

const getSelectedCustomer = (customer) => {
  selectedCustomer.value = customer || null
  /*order.customer = customer
      ? customer.name ? customer.name : customer.storeID
      : null*/
  order.customerID = customer ? customer.id : null
}

function showCustomerProfileModal(user) {
  if (user.isLoading) return;
  if (canShowCustomerProfile.value) {
    customerStore.setSelectedCustomer(user);
    isShowCustomerProfileModal.value = true
  }
}

function applyUsdRate(rateName) {
  const currency = currencyPrices.value.find(currency => currency.name === rateName)
  if (!currency) return false

  activeUsdRateName.value = rateName
  order.usd_tmn = currency.price
  return true
}

function toggleUsdRate() {
  if (!applyUsdRate(nextUsdRateName.value)) {
    showMessage(`${nextUsdRateName.value} price not found`, 'error')
  }
}

const getCurrencies = async (refresh) => {
  try {
    if (!refresh) setFetching(true)
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      currencyPrices.value = res.data;
      applyUsdRate('Toman Shop')
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setFetching(false)
  }
}

/* ---------- واکر برای ورودی‌های کلیدی ---------- */
watch(
    () => [order.usd_tmn, order.tomanPrice, order.tomanCost],
    recalc,
    {immediate: true}
)

onMounted(() => {
  getCurrencies(false)
})

provide('isShowCustomerProfileModal', isShowCustomerProfileModal)
</script>

<template>
  <div class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading && fetching" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>

        <!-- Order details form -->
        <TabPanels v-else class="h-full">
          <div class="grid grid-cols-5 gap-6 h-full">
            <div class="col-span-4 flex flex-col gap-4 relative h-full overflow-hidden px-2">
              <div class="flex flex-row gap-2 items-center justify-between">
                <div class="flex flex-row gap-2 items-center">
                  <Tooltip placement="top" arrow>
                    <template #button>
                      <div class="back-btn" @click="backToOrders">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                        </svg>
                      </div>
                    </template>
                    <span>{{ $t('message.back') }}</span>
                  </Tooltip>
                  <TabList class="">
                    <Tab
                        v-slot="{ selected }"
                        as="template"
                        v-for="(item, i) in tabs"
                        :key="i"
                    >
                      <button
                          :class="[
                            selected
                              ? 'text-white bg-primary-700'
                              : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                          ]"
                          class="text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-6 py-2 transition duration-150 rounded-none first:rounded-l-2xl  last:rounded-r-2xl  "
                      >
                        {{ item.title }}
                      </button>
                    </Tab>
                  </TabList>
                </div>
                <div class="flex flex-row items-center gap-2">
                  <div class="inline-block w-max relative" ref="stateDropdownRef">
                    <button @click="toggleStateDropdown"
                            :class="{ '!border !border-red-300': $vulidate.state.$error }"
                            class="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md px-2 py-2 text-left flex justify-between items-center flex-row gap-2">
                      <div class="flex items-center gap-2">
                        <span class="w-2 h-2 rounded-full" :class="stateColorsMap[order.state]"></span>
                        {{ order.state }}
                      </div>
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </button>
                    <div v-if="isStateOpen"
                         class="absolute right-0 z-10 mt-1 w-max min-w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md shadow-lg">
                      <ul>
                        <li v-for="state in states" :key="state" @click="selectState(state)"
                            class="px-2 py-2 hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[state]"></span>
                          {{ state }}
                        </li>
                      </ul>
                    </div>
                    <template v-if="$vulidate.state.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.state.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>
                </div>
              </div>
              <hr class="border-0 h-0.5 bg-slate-800">
              <TabPanel :unmount="false"
                        class="flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2">
                <div class="grid grid-cols-12 gap-4 pt-2">
                  <!-- ================== Title ================== -->
                  <div class="col-span-8 flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <FloatLabel variant="on">
                      <InputText id="title" v-model="order.title" autocomplete="off"
                                 class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                 :class="{ 'has-error': $vulidate.title.$error }"/>
                      <label for="title" class="!bg-slate-900"
                             :class="{ 'focus:text-red-300': $vulidate.title.$error }">Title</label>
                    </FloatLabel>
                    <template v-if="$vulidate.title.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.title.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== USD/TMN ================== -->
                  <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on">
                        <InputText v-allow-digits-dot id="usd_tmn" v-model="order.usd_tmn" autocomplete="off"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error has-addon': $vulidate.usd_tmn.$error }"/>
                        <label for="usd_tmn" class="!bg-slate-900"
                               :class="{ 'has-error': $vulidate.usd_tmn.$error }">USD/TMN</label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-transparent !border-slate-700 h-full !p-0">
                        <button type="button" @click="toggleUsdRate" :disabled="!canToggleUsdRate"
                                :title="`Use ${nextUsdRateName} rate`" :aria-label="`Use ${nextUsdRateName} rate`"
                                class="h-full px-2 flex items-center justify-center hover:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                          <Icon icon="lucide:arrow-left-right" class="size-4"/>
                        </button>
                      </InputGroupAddon>
                    </InputGroup>
                    <template v-if="$vulidate.usd_tmn.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.usd_tmn.required.$response">
                        This field is required
                      </p>
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.usd_tmn.numeric.$response">
                        This field should be a number
                      </p>
                    </template>
                  </div>

                  <!-- ================== Reminder Time (USD) ================== -->
                  <div class="col-span-2 flex-1 flex flex-col gap-0.5 text-sm">
                    <FloatLabel variant="on">
                      <DatePicker
                          inputId="reminderTime"
                          v-model="reminderTimeModel"
                          showTime
                          hourFormat="24"
                          dateFormat="d M yy"
                          fluid
                          :showButtonBar="true"
                          class="flex-1"
                          size="small"
                          input-class="!border-slate-700 !bg-transparent h-[40px]"
                          ref="pickerRef"
                          :readonly="false"
                          showIcon iconDisplay="input"
                      >
                        <template #todaybutton>
                            <span @click="selectNow"
                                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Now</span>
                        </template>
                        <template #clearbutton>
                            <span @click="clearReminderTime"
                                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Clear</span>
                        </template>
                      </DatePicker>
                      <label for="reminderTime" class="!bg-slate-900">Reminder Time</label>
                    </FloatLabel>
                  </div>

                  <!-- ================== Price (USD) ================== -->
                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on">
                        <InputText v-allow-digits-dot id="price" v-model="order.price" autocomplete="off"
                                   :readonly="priceInputSource === 'tomanPrice'"
                                   @input="handlePriceInput($event.target.value)"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                        <label for="price" :class="[order.price ? '!bg-slate-900' : '!bg-transparent']">Price</label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">
                        <span>$</span>
                      </InputGroupAddon>
                    </InputGroup>
                  </div>

                  <!-- ================== Cost (USD) ================== -->
                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on">
                        <InputText v-allow-digits-dot id="cost" v-model="order.cost" autocomplete="off"
                                   :readonly="costInputSource === 'tomanCost'"
                                   @input="handleCostInput($event.target.value)"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                        <label for="cost" :class="[order.cost ? '!bg-slate-900' : '!bg-transparent']">Cost</label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">
                        <span>$</span>
                      </InputGroupAddon>
                    </InputGroup>
                  </div>

                  <!-- ================== Revenue ================== -->
                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on">
                        <InputText v-allow-digits-dot id="revenue" v-model="order.revenue" autocomplete="off" readonly
                                   disabled
                                   class="!bg-slate-800 w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                        <label for="revenue"
                               :class="[order.revenue ? '!bg-slate-900' : '!bg-transparent']">Revenue</label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-slate-800 !border-slate-700 h-full">
                        <span>$</span>
                      </InputGroupAddon>
                    </InputGroup>
                  </div>

                  <!-- ================== Toman Price ================== -->
                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on" :class="{ 'has-error': $vulidate.tomanPrice.$error }">
                        <InputText v-allow-digits-dot id="tPrice" v-model="order.tomanPrice" autocomplete="off"
                                   :readonly="priceInputSource === 'price'"
                                   @input="handleTomanPriceInput($event.target.value)"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error has-addon': $vulidate.tomanPrice.$error }"/>
                        <label for="tPrice" class="!bg-slate-900"
                               :class="{ 'has-error': $vulidate.tomanPrice.$error }">Toman Price</label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">
                        <span class="whitespace-nowrap"><span class="text-[0.6rem]">* 1000</span> Tmn</span>
                      </InputGroupAddon>
                    </InputGroup>
                    <template v-if="$vulidate.tomanPrice.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tomanPrice.required.$response">
                        This field is required
                      </p>
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tomanPrice.numeric.$response">
                        This field should be a number
                      </p>
                    </template>
                  </div>

                  <!-- ================== Toman Cost ================== -->
                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on" :class="{ 'has-error': $vulidate.tomanCost.$error }">
                        <InputText v-allow-digits-dot id="tCost" v-model="order.tomanCost" autocomplete="off"
                                   :readonly="costInputSource === 'cost'"
                                   @input="handleTomanCostInput($event.target.value)"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error has-addon': $vulidate.tomanCost.$error }"/>
                        <label for="tCost" class="!bg-slate-900"
                               :class="{ 'has-error': $vulidate.tomanCost.$error }">Toman Cost</label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">
                        <span class="whitespace-nowrap"><span class="text-[0.6rem]">* 1000</span> Tmn</span>
                      </InputGroupAddon>
                    </InputGroup>
                    <template v-if="$vulidate.tomanCost.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tomanCost.required.$response">
                        This field is required
                      </p>
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tomanCost.numeric.$response">
                        This field should be a number
                      </p>
                    </template>
                  </div>

                  <!-- ================== Toman Revenue ================== -->
                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <InputGroup>
                      <FloatLabel variant="on">
                        <InputText v-allow-digits-dot id="tRevenue" v-model="order.tomanRevenue" autocomplete="off"
                                   readonly disabled
                                   class="!bg-slate-800 w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                        <label for="tRevenue" :class="[order.tomanRevenue ? '!bg-slate-900' : '!bg-transparent']">
                          Toman Revenue
                        </label>
                      </FloatLabel>
                      <InputGroupAddon class="!bg-slate-800 !border-slate-700 h-full">
                        <span class="whitespace-nowrap"><span class="text-[0.6rem]">* 1000</span> Tmn</span>
                      </InputGroupAddon>
                    </InputGroup>
                  </div>

                  <div class="col-span-4 flex flex-row gap-2 text-xs 2xl:text-sm">
                    <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                      <Customers v-model="selectedCustomer" @update:modelValue="getSelectedCustomer" appendTo="body"
                                 label="Customer" :required="true"/>
                    </div>
                  </div>
                  <div v-if="selectedCustomer?.id"
                       class="col-span-8 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md px-2 py-1 h-[32px] 2xl:h-[40px]">
                    <div @click.stop="showCustomerProfileModal(selectedCustomer)"
                         :class="['h-full flex items-center', canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
                      <div
                          class="flex items-center justify-center rounded-full border border-slate-700 h-full aspect-square">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1 grid grid-cols-2 gap-1">
                      <div class="flex flex-col">
                        <div class="flex flex-row gap-6 text-xs">
                          <div>{{ selectedCustomer.name || selectedCustomer.storeID }}</div>
                          <div v-if="selectedCustomer.rating" class="flex flex-row items-center gap-0.5">
                                <span class="flex flex-row items-end">
                                  {{ selectedCustomer.rating }}⭐ ({{ selectedCustomer.ratingCount }})
                                </span>
                          </div>
                        </div>
                        <div class="flex flex-row w-max text-xs text-slate-400">
                          <div>{{ selectedCustomer.discordUsername ?? "ــــــ" }}</div>
                          <div>
                            {{
                              "&nbsp- " + (selectedCustomer.discordDisplayName ? selectedCustomer.discordDisplayName : "ــــــ")
                            }}
                          </div>
                        </div>
                      </div>
                      <div class="flex flex-col text-xs">
                        <div>{{ selectedCustomer.verificationState ?? "ــــــ" }}</div>
                        <div>
                          {{
                            selectedCustomer.registrationTime ? gregorianDate(selectedCustomer.registrationTime, 'DD MMM YYYY - HH:mm') : ''
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm h-full">
                  <FloatLabel variant="on" class="h-full">
                    <Textarea v-auto-dir id="notes" v-model="order.notes" rows="5" style="resize: none"
                              class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full block"/>
                    <label for="notes" class="!bg-slate-900">Notes</label>
                  </FloatLabel>
                </div>
              </TabPanel>
              <TabPanel :unmount="false"
                        class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2">
                <!-- Only changes -->
                <Attachments
                    :count="16"
                    :only-changes="true"
                    v-model:uploading="isUploadingAttachment"
                    @update:payload="val => (attachments = val)"
                />
              </TabPanel>
              <hr class="border-0 h-0.5 bg-slate-800">
              <div class="flex flex-row gap-4 justify-end items-center">
                <button @click="onSave" class="btn btn-outline-primary py-2" :disabled="saving">
                  <span v-if="saving"
                        class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                    <Loading/>
                  </span>
                  Save
                </button>
              </div>
            </div>
            <div class="col-span-1 h-full overflow-auto custom-scrollbar-2 rounded-lg pr-2">
              <div class="bg-slate-700 p-2 flex flex-col gap-10 rounded-lg overflow-hidden">
                <h6 class="text-center text-base py-2 bg-slate-900 rounded-lg">Shop</h6>
                <div class="flex flex-col gap-1 h-max">
                  <p>Title: {{ order.title }}</p>
                  <p>Customer: {{ selectedCustomer?.name || selectedCustomer?.storeID }}</p>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <CustomerProfile v-if="isShowCustomerProfileModal" :userID="customerStore.selectedCustomer.id"/>
  </div>
</template>

<style>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-8 w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}

.p-datepicker-header {
  @apply !bg-slate-800
}

.p-datepicker-day-view {
  @apply !text-sm;
}

.p-datepicker-weekday-cell {
  @apply text-center
}

.p-datepicker-panel {
  @apply !bg-slate-800 border border-slate-700
}

.p-inputtext.reminderTimeError {
  @apply !border !border-red-300;
}

.p-inputtext:not(.reminderTimeError):enabled:focus {
  @apply !border-slate-700;
}
</style>
